import { yupResolver } from '@hookform/resolvers/yup';
import { Box, Grid, TextField } from '@mui/material';

import PropTypes from 'prop-types';
import { useState } from 'react';
import { useForm, FormProvider } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { v4 as uuidv4 } from 'uuid';

import { acPostCreditPolicy, acPutCreditPolicy } from '../../store/actions';
import { ROUTES } from '../../store/constants';
import { Paper, PrimaryButton, Alert, CleanButton } from '../default';

import { ScoreRangeForm } from './ScoreRangeForm';
import { FormCheckbox, FormInputText, FormInputTextCharacterLimit } from './formInputs';
import { FormValidationCreateCreditPolicy } from './formValidations';

export const CreditPolicyForm = ({ selectedCreditPolicy }) => {

    /** Default Form Values */
    const defaultValues = {
        creditPolicyId: selectedCreditPolicy?.credit_policy_id || null,
        name: selectedCreditPolicy?.name || '',
        description: selectedCreditPolicy?.description || '',
        isActive: selectedCreditPolicy ? selectedCreditPolicy.is_active : true,
        scoreRanges:
            selectedCreditPolicy?.scoreRanges?.map((scoreRange) => ({
                scoreRangeId: scoreRange.score_range_id,
                minScore: scoreRange.min_score,
                maxScore: scoreRange.max_score,
                creditLimitMinValue: scoreRange.credit_limit_min_value,
                creditLimitMaxValue: scoreRange.credit_limit_max_value,
                consumptionRanges: scoreRange.consumptionRanges?.map((consumptionRange) => ({
                    consumptionRangeId: consumptionRange.consumption_range_id,
                    minValue: consumptionRange.min_value,
                    maxValue: consumptionRange.max_value,
                    action: consumptionRange.action,
                    color: consumptionRange.color
                })) || []
            })) || [{
                scoreRangeId: uuidv4(),
                minScore: 0,
                maxScore: '',
                creditLimitMaxValue: null,
                consumptionRanges: []
            }]
    }

    const navigate = useNavigate()
    const dispatch = useDispatch()

    /** Local States */
    const [creditPolicyError, setCreditPolicyError] = useState(null)

    /** Global States */
    const isCreatingCreditPolicy = useSelector(state => state.creditPolicies.isCreatingCreditPolicy)
    const hasErrorCreatedCreditPolicy = useSelector(state => state.creditPolicies.hasErrorCreatedCreditPolicy)

    const isUpdatingCreditPolicy = useSelector(state => state.creditPolicies.isUpdatingCreditPolicy)
    const hasErrorUpdatedCreditPolicy = useSelector(state => state.creditPolicies.hasErrorUpdatedCreditPolicy)

    /** Form Hooks */
    const formProps = useForm({
        defaultValues: defaultValues,
        resolver: yupResolver(FormValidationCreateCreditPolicy)
    })

    const { control, handleSubmit, formState: { errors } } = formProps

    /** Validate Functions */
    const validateScoreRanges = scoreRanges => {
        if (!scoreRanges || scoreRanges.length === 0)
            return false

        const sortedRanges = [...scoreRanges].sort((a, b) => a.minScore - b.minScore)

        if (sortedRanges[0].minScore !== 0)
            return false

        for (let i = 0; i < sortedRanges.length - 1; i++) {
            if (sortedRanges[i].maxScore + 1 !== sortedRanges[i + 1].minScore) {
                return false
            }
        }

        if (sortedRanges[sortedRanges.length - 1].maxScore !== 1000)
            return false

        return true
    }

    const validateConsumptionRanges = (scoreRanges) => {
        return scoreRanges.every(sr => (sr.consumptionRanges?.length ?? 0) > 0)
    }

    /** Submit Handler */
    const onSubmit = handleSubmit(async (data) => {

        setCreditPolicyError(null)

        if (!validateScoreRanges(data.scoreRanges)) {
            setCreditPolicyError('É necessário que os intervalos das faixas cubram os scores de 0 a 1000!')
            return
        }

        if (!validateConsumptionRanges(data.scoreRanges)) {
            setCreditPolicyError('É necessário definir as regras para todas as faixas de score!')
            return
        }

        const body = {
            name: data.name,
            description: data.description,
            is_active: data.isActive,
            scoreRanges: data.scoreRanges.map(scoreRange => ({
                score_range_id: scoreRange.scoreRangeId,
                min_score: scoreRange.minScore,
                max_score: scoreRange.maxScore,
                credit_limit_min_value: scoreRange.creditLimitMinValue,
                credit_limit_max_value: scoreRange.creditLimitMaxValue,
                consumptionRanges: scoreRange.consumptionRanges.map(consumptionRange => ({
                    consumption_range_id: consumptionRange.consumptionRangeId,
                    min_value: consumptionRange.minValue,
                    max_value: consumptionRange.maxValue,
                    action: consumptionRange.action,
                    color: consumptionRange.color
                }))
            }))
        }

        if (selectedCreditPolicy) {

            dispatch(acPutCreditPolicy(data.creditPolicyId, body))
                .then(() => {
                    navigate(ROUTES.CREDIT_POLICIES_LIST, {
                        state: {
                            successAlert: { open: true, message: 'Política de Crédito alterada com sucesso!' }
                        }
                    })
                })
                .catch(error => console.error(error))

        } else {

            dispatch(acPostCreditPolicy(body))
                .then(() => {
                    navigate(ROUTES.CREDIT_POLICIES_LIST, {
                        state: {
                            successAlert: { open: true, message: 'Política de Crédito criada com sucesso!' }
                        }
                    })
                })
                .catch(error => console.error(error))
        }
    })

    return (
        <FormProvider {...formProps}>
            <Box
                component="form"
                onSubmit={onSubmit}
                noValidate
                width="100%"
                display="flex"
                flexDirection="column"
                gap={3}
            >

                <Paper alignItems="flex-start">

                    {selectedCreditPolicy ? (
                        <Grid container alignItems="center" spacing={2}>
                            <Grid item xs='auto'>
                                <TextField
                                    label="ID"
                                    value={`# ${selectedCreditPolicy?.credit_policy_id}`}
                                    variant="outlined"
                                    disabled
                                />
                            </Grid>
                            <Grid item flexGrow={1}>
                                <FormInputText
                                    name="name"
                                    label="Nome"
                                    control={control}
                                    errors={errors.name}
                                    fullWidth
                                    required
                                />
                            </Grid>
                        </Grid>
                    ) : (
                        <FormInputText
                            name="name"
                            label="Nome"
                            control={control}
                            errors={errors.name}
                            fullWidth
                            required
                        />
                    )}

                    <FormInputTextCharacterLimit
                        name="description"
                        label="Descrição"
                        control={control}
                        errors={errors.description}
                        fullWidth
                        multiline
                        rows={3}
                        required
                    />

                    <FormCheckbox
                        name="isActive"
                        label="Definir como política padrão"
                        labelPlacement="end"
                        control={control}
                    />
                </Paper>

                <Paper alignItems="flex-start">

                    <ScoreRangeForm />

                    {creditPolicyError && (
                        <Alert severity="warning" message={creditPolicyError} />
                    )}

                    {(hasErrorCreatedCreditPolicy || hasErrorUpdatedCreditPolicy) && (
                        <Alert
                            severity="error"
                            message={`Erro ao ${selectedCreditPolicy ? 'alterar' : 'criar'} política de crédito!`}
                        />
                    )}

                </Paper>

                <Box width="100%" display="flex" justifyContent="right" gap={2}>
                    <CleanButton
                        label='Cancelar'
                        onClick={() => navigate(ROUTES.CREDIT_POLICIES_LIST)}
                    />

                    <PrimaryButton
                        type="submit"
                        label={selectedCreditPolicy ? 'Salvar Política de Crédito' : 'Criar Política de Crédito'}
                        loading={isCreatingCreditPolicy || isUpdatingCreditPolicy}
                        disabled={!formProps.formState.isValid}
                    />
                </Box>
            </Box>
        </FormProvider>
    )
}

CreditPolicyForm.propTypes = {
    selectedCreditPolicy: PropTypes.object
}
